import React, { useState } from "react";

interface HealthRecommendationsProps {
  destination: any;
}

const HealthRecommendations: React.FC<HealthRecommendationsProps> = ({
  destination,
}) => {
  const [expandedSections, setExpandedSections] = useState({
    critical: true,
    conditionalEntry: true,
    medications: true,
    vaccines: false,
    environmental: false,
    details: false,
  });

  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));

    // Scroll to the section after a brief delay to allow expansion animation
    setTimeout(() => {
      const element = document.getElementById(`section-${section}`);
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }, 100);
  };

  const healthRecs = destination?.health_recommendations;
  if (!healthRecs) return null;

  // Priority order: Critical vaccines -> Conditional Entry -> Medications -> Environmental -> Other vaccines
  const getPriorityData = () => {
    const priority: {
      critical: any[];
      conditionalEntry: any[];
      medications: { required: any[]; conditional: any[] };
      environmental: any[];
      vaccines: { required: any[]; recommended: any[]; conditional: any[] };
    } = {
      critical: [],
      conditionalEntry: [],
      medications: {
        required: [],
        conditional: [],
      },
      environmental: [],
      vaccines: {
        required: [],
        recommended: [],
        conditional: [],
      },
    };

    // Process vaccines with proper Yellow Fever handling
    healthRecs.vaccine_details?.required?.forEach((vaccine: any) => {
      if (vaccine.risk_level === "critical") {
        priority.critical.push(vaccine);
      } else {
        priority.vaccines.required.push(vaccine);
      }
    });

    // Process conditional vaccines - separate conditional entry from others
    healthRecs.vaccine_details?.conditional?.forEach((vaccine: any) => {
      if (vaccine.risk_level === "conditional_entry") {
        priority.conditionalEntry.push(vaccine);
      } else {
        priority.vaccines.conditional.push(vaccine);
      }
    });

    // Process recommended vaccines
    healthRecs.vaccine_details?.recommended?.forEach((vaccine: any) => {
      priority.vaccines.recommended.push(vaccine);
    });

    // Process regular medications
    healthRecs.medications?.forEach((med: any) => {
      priority.medications.required.push(med);
    });

    // Process conditional medications
    healthRecs.conditional_medications?.forEach((med: any) => {
      priority.medications.conditional.push(med);
    });

    // Get environmental risks (altitude, travelers diarrhea)
    if (healthRecs.disease_risks?.altitude_sickness) {
      priority.environmental.push(healthRecs.disease_risks.altitude_sickness);
    }
    if (healthRecs.disease_risks?.travelers_diarrhea) {
      priority.environmental.push(healthRecs.disease_risks.travelers_diarrhea);
    }

    return priority;
  };

  const priorityData = getPriorityData();

  const getRiskLevelColor = (level: string) => {
    const colors: Record<string, string> = {
      critical: "#fef2f2",
      conditional_entry: "#fffbeb",
      high: "#fef2f2",
      essential: "#fff7ed",
      important: "#fefce8",
      consider: "#eff6ff",
      moderate: "#fffbeb",
      low: "#f0fdf4",
      not_recommended: "#f9fafb",
    };
    return colors[level] || "#f9fafb";
  };

  const getRiskBorderColor = (level: string) => {
    const colors: Record<string, string> = {
      critical: "#dc2626",
      conditional_entry: "#f59e0b",
      high: "#dc2626",
      essential: "#ea580c",
      important: "#eab308",
      consider: "#2563eb",
      moderate: "#f59e0b",
      low: "#16a34a",
      not_recommended: "#6b7280",
    };
    return colors[level] || "#6b7280";
  };

  const getRiskBadge = (level: string) => {
    const badges: Record<string, { text: string; bgColor: string }> = {
      critical: { text: "REQUIRED FOR ENTRY", bgColor: "#dc2626" },
      conditional_entry: { text: "ROUTE-DEPENDENT", bgColor: "#f59e0b" },
      high: { text: "High Risk", bgColor: "#dc2626" },
      essential: { text: "Essential", bgColor: "#ea580c" },
      important: { text: "Recommended", bgColor: "#eab308" },
      consider: { text: "Consider", bgColor: "#2563eb" },
      moderate: { text: "Moderate Risk", bgColor: "#f59e0b" },
      low: { text: "Low Risk", bgColor: "#16a34a" },
      not_recommended: { text: "Not Needed", bgColor: "#6b7280" },
    };
    const badge = badges[level] || { text: level, bgColor: "#6b7280" };
    return (
      <span
        style={{
          padding: "4px 8px",
          borderRadius: "4px",
          fontSize: "11px",
          fontWeight: "600",
          backgroundColor: badge.bgColor,
          color: "white",
        }}
      >
        {badge.text}
      </span>
    );
  };

  const renderVaccineCard = (vaccine: any) => {
    return (
      <div
        key={vaccine.vaccine_code}
        style={{
          padding: "16px",
          marginBottom: "12px",
          borderRadius: "8px",
          backgroundColor: getRiskLevelColor(vaccine.risk_level),
          borderLeft: `4px solid ${getRiskBorderColor(vaccine.risk_level)}`,
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            flexWrap: "wrap",
            gap: "8px",
            marginBottom: "8px",
          }}
        >
          <h4 style={{ margin: 0, fontWeight: "600", fontSize: "16px" }}>
            {vaccine.vaccine}
          </h4>
          {getRiskBadge(vaccine.risk_level)}
        </div>

        {/* CDC clinical guidance from API */}
        {vaccine.clinical_guidance && (
          <div
            style={{
              fontSize: "14px",
              lineHeight: "1.6",
              color: "#4b5563",
              marginBottom: "8px",
            }}
          >
            {vaccine.clinical_guidance}
          </div>
        )}

        {vaccine.summary && (
          <div
            style={{
              fontSize: "13px",
              color: "#6b7280",
              marginTop: "6px",
            }}
          >
            {vaccine.summary}
          </div>
        )}
      </div>
    );
  };

  const renderMedicationSection = (medication: any) => {
    return (
      <div
        key={medication.disease_code}
        style={{
          padding: "16px",
          marginBottom: "12px",
          borderRadius: "8px",
          backgroundColor: getRiskLevelColor(medication.risk_level),
          borderLeft: `4px solid ${getRiskBorderColor(medication.risk_level)}`,
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            flexWrap: "wrap",
            gap: "8px",
            marginBottom: "12px",
          }}
        >
          <h3 style={{ margin: 0, fontWeight: "600", fontSize: "18px" }}>
            {medication.disease} Prevention
          </h3>
          {getRiskBadge(medication.risk_level)}
        </div>

        {/* CDC recommendation data from API */}
        {medication.recommendation_data?.customer_summary && (
          <div
            style={{
              marginBottom: "12px",
              padding: "12px",
              backgroundColor: "rgba(255, 255, 255, 0.5)",
              borderRadius: "6px",
              border: "1px solid #e5e7eb",
            }}
          >
            <p style={{ fontSize: "14px", lineHeight: "1.6", margin: 0 }}>
              {medication.recommendation_data.customer_summary}
            </p>
          </div>
        )}

        {medication.special_conditions && (
          <div
            style={{
              marginBottom: "12px",
              padding: "8px",
              backgroundColor: "#fffbeb",
              borderRadius: "6px",
            }}
          >
            <p style={{ fontSize: "14px", color: "#92400e", margin: 0 }}>
              <strong>Note:</strong> {medication.special_conditions}
            </p>
          </div>
        )}

        {medication.medications?.prophylaxis_drugs?.length > 0 && (
          <div style={{ marginTop: "12px" }}>
            <p
              style={{
                fontSize: "14px",
                fontWeight: "500",
                marginBottom: "8px",
              }}
            >
              Prophylaxis Medications (prescription required):
            </p>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
              {medication.medications.prophylaxis_drugs.map(
                (drug: string, idx: number) => (
                  <div
                    key={idx}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                      padding: "8px 12px",
                      backgroundColor: "rgba(255, 255, 255, 0.7)",
                      borderRadius: "6px",
                    }}
                  >
                    <span
                      style={{ fontSize: "14px", textTransform: "capitalize" }}
                    >
                      {drug.replace(/-/g, " ")}
                    </span>
                  </div>
                ),
              )}
            </div>
          </div>
        )}

        {/* City-specific medication requirements - formatted */}
        {(medication.recommendation_data?.conditions?.full_text ||
          medication.recommendation_data?.conditions?.details) && (
          <div
            style={{
              marginTop: "12px",
              padding: "16px",
              backgroundColor: "#f8fafc",
              border: "1px solid #e2e8f0",
              borderRadius: "8px",
            }}
          >
            {/* CDC Intro */}
            <div
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: "8px",
                marginBottom: "16px",
                padding: "12px",
                backgroundColor: "#dbeafe",
                borderRadius: "6px",
                border: "1px solid #3b82f6",
              }}
            >
              <span style={{ fontSize: "18px" }}>📋</span>
              <p
                style={{
                  fontSize: "14px",
                  color: "#1e40af",
                  margin: 0,
                  lineHeight: "1.6",
                }}
              >
                CDC recommends that travelers going to certain areas take
                prescription medicine to prevent malaria. Depending on the
                medicine you take, you will need to start taking this medicine
                multiple days before your trip, as well as during and after your
                trip. Talk to your doctor about which malaria medication you
                should take.
              </p>
            </div>

            {/* Combined Malaria Information - Paragraph with bullet points */}
            {(() => {
              const details =
                medication.recommendation_data?.conditions?.details || "";
              const fullText =
                medication.recommendation_data?.conditions?.full_text || "";

              // Helper to remove footnote numbers from end of text
              const stripFootnotes = (text: string) =>
                text.replace(/\d+$/g, "").trim();

              // Parse transmission areas
              const allAreas = details
                .split(/[;\n]/)
                .map((s: string) => stripFootnotes(s.trim()))
                .filter((s: string) => {
                  if (s.length === 0) return false;
                  const lower = s.toLowerCase();
                  if (lower === "chloroquine") return false;
                  if (lower === "none") return false;
                  if (lower.startsWith("p. vivax")) return false;
                  if (lower.startsWith("p. falciparum")) return false;
                  if (lower.includes("atovaquone")) return false;
                  if (lower.includes("doxycycline")) return false;
                  if (lower.includes("mefloquine")) return false;
                  if (lower.includes("tafenoquine")) return false;
                  if (lower.includes("primaquine")) return false;
                  if (lower.includes("no chemoprophylaxis")) return false;
                  return true;
                });

              // Categorize areas
              const highRiskAreas: string[] = [];
              const noRiskAreas: string[] = [];

              allAreas.forEach((area: string) => {
                const lower = area.toLowerCase();
                if (
                  lower.includes("no malaria") ||
                  lower.includes("no transmission")
                ) {
                  noRiskAreas.push(area);
                } else {
                  highRiskAreas.push(area);
                }
              });

              // Extract drug resistance
              let drugResistance = "";
              const drugMatchFT = fullText.match(
                /Drug resistance\n(.*?)(?=\nSpecies|$)/is,
              );
              if (drugMatchFT) {
                drugResistance = drugMatchFT[1].trim();
              }
              if (!drugResistance && details) {
                const parts = details.split(";").map((s: string) => s.trim());
                const drugPart = parts.find(
                  (p: string) =>
                    p.toLowerCase() === "chloroquine" ||
                    p.toLowerCase().includes("chloroquine-resistant") ||
                    p.toLowerCase() === "none",
                );
                if (drugPart) drugResistance = drugPart;
              }

              // Extract species
              let speciesList: string[] = [];
              const speciesMatchFT = fullText.match(
                /Species\n([\s\S]*?)(?=\nRecommended chemoprophylaxis|$)/i,
              );
              if (speciesMatchFT) {
                speciesList = speciesMatchFT[1]
                  .split("\n")
                  .filter((line: string) => line.trim())
                  .map((s: string) => s.trim());
              }
              if (speciesList.length === 0 && details) {
                const parts = details.split(";").map((s: string) => s.trim());
                speciesList = parts.filter(
                  (p: string) =>
                    p.toLowerCase().includes("p. vivax") ||
                    p.toLowerCase().includes("p. falciparum") ||
                    p.toLowerCase().includes("plasmodium"),
                );
              }

              // Build bullet points
              const bulletPoints: string[] = [];

              // Add risk areas
              if (highRiskAreas.length > 0) {
                bulletPoints.push(`Risk Areas: ${highRiskAreas.join("; ")}`);
              }

              // Add safe areas
              if (noRiskAreas.length > 0) {
                bulletPoints.push(`Safe Areas (No Malaria): ${noRiskAreas.join("; ")}`);
              }

              // Add drug resistance
              if (drugResistance) {
                bulletPoints.push(`Drug Resistance: ${drugResistance}`);
              }

              // Add species
              if (speciesList.length > 0) {
                bulletPoints.push(`Malaria Species: ${speciesList.join(", ")}`);
              }

              if (bulletPoints.length === 0) return null;

              return (
                <div
                  style={{
                    marginBottom: "16px",
                    padding: "16px",
                    backgroundColor: "#fef3c7",
                    borderRadius: "8px",
                    borderLeft: "4px solid #f59e0b",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      marginBottom: "12px",
                    }}
                  >
                    <span style={{ fontSize: "18px" }}>📍</span>
                    <p
                      style={{
                        fontSize: "15px",
                        fontWeight: "700",
                        color: "#92400e",
                        margin: 0,
                      }}
                    >
                      Malaria Risk Information
                    </p>
                  </div>
                  <ul
                    style={{
                      margin: 0,
                      paddingLeft: "24px",
                      listStyleType: "disc",
                    }}
                  >
                    {bulletPoints.map((point: string, idx: number) => (
                      <li
                        key={idx}
                        style={{
                          fontSize: "13px",
                          color: "#78350f",
                          marginBottom: "8px",
                          lineHeight: "1.6",
                        }}
                      >
                        {point}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })()}

            {/* Recommended Chemoprophylaxis */}
            {(() => {
              const details =
                medication.recommendation_data?.conditions?.details || "";
              const fullText =
                medication.recommendation_data?.conditions?.full_text || "";

              // Helper to remove footnote numbers (e.g., "text2" or "text4" at end)
              const stripFootnotes = (text: string) =>
                text.replace(/\d+$/g, "").trim();

              const prophylaxisItems: string[] = [];
              const noteItems: string[] = [];

              // Try from full_text first
              const prophylaxisMatch = fullText.match(
                /Recommended chemoprophylaxis\n([\s\S]*?)(?=\nSee footnotes|$)/i,
              );
              if (prophylaxisMatch) {
                const items = prophylaxisMatch[1]
                  .split("\n")
                  .filter((line: string) => line.trim());
                items.forEach((item: string) => {
                  const cleanItem = stripFootnotes(item.trim());
                  if (
                    cleanItem.toLowerCase().includes("rare cases") ||
                    cleanItem.toLowerCase().includes("no chemoprophylaxis")
                  ) {
                    noteItems.push(cleanItem);
                  } else if (cleanItem) {
                    prophylaxisItems.push(cleanItem);
                  }
                });
              }

              // Try from details if nothing found
              if (prophylaxisItems.length === 0 && details) {
                const parts = details.split(";").map((s: string) => s.trim());
                parts.forEach((part: string) => {
                  const cleanPart = stripFootnotes(part);
                  // Look for drug names
                  if (
                    cleanPart.toLowerCase().includes("atovaquone") ||
                    cleanPart.toLowerCase().includes("doxycycline") ||
                    cleanPart.toLowerCase().includes("mefloquine") ||
                    cleanPart.toLowerCase().includes("tafenoquine") ||
                    cleanPart.toLowerCase().includes("primaquine")
                  ) {
                    prophylaxisItems.push(cleanPart);
                  }
                  // Look for notes about rare cases
                  if (
                    cleanPart.toLowerCase().includes("rare cases") ||
                    cleanPart.toLowerCase().includes("no chemoprophylaxis")
                  ) {
                    noteItems.push(cleanPart);
                  }
                });
              }

              if (prophylaxisItems.length === 0 && noteItems.length === 0)
                return null;

              return (
                <div>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                      marginBottom: "8px",
                    }}
                  >
                    <span style={{ fontSize: "16px" }}>💊</span>
                    <p
                      style={{
                        fontSize: "14px",
                        fontWeight: "700",
                        color: "#1e40af",
                        margin: 0,
                      }}
                    >
                      Recommended Chemoprophylaxis
                    </p>
                  </div>
                  <div
                    style={{
                      backgroundColor: "#eff6ff",
                      padding: "12px",
                      borderRadius: "6px",
                      borderLeft: "4px solid #2563eb",
                    }}
                  >
                    {prophylaxisItems.map((item: string, idx: number) => (
                      <p
                        key={idx}
                        style={{
                          fontSize: "13px",
                          color: "#1e3a8a",
                          margin: "0 0 6px 0",
                          fontWeight: "600",
                        }}
                      >
                        • {item}
                      </p>
                    ))}
                    {noteItems.map((item: string, idx: number) => (
                      <p
                        key={`note-${idx}`}
                        style={{
                          fontSize: "13px",
                          color: "#92400e",
                          margin: "6px 0 0 0",
                          fontWeight: "500",
                          fontStyle: "italic",
                          backgroundColor: "#fef3c7",
                          padding: "6px 8px",
                          borderRadius: "4px",
                        }}
                      >
                        ⚠️ {item}
                      </p>
                    ))}
                  </div>
                </div>
              );
            })()}
          </div>
        )}
      </div>
    );
  };

  const renderEnvironmentalRisk = (risk: any) => {
    return (
      <div
        key={risk.disease_name}
        style={{
          padding: "16px",
          marginBottom: "12px",
          borderRadius: "8px",
          backgroundColor: getRiskLevelColor(risk.risk_level),
          borderLeft: `4px solid ${getRiskBorderColor(risk.risk_level)}`,
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            flexWrap: "wrap",
            gap: "8px",
            marginBottom: "8px",
          }}
        >
          <h4 style={{ margin: 0, fontWeight: "600", fontSize: "16px" }}>
            {risk.disease_name}
          </h4>
          {getRiskBadge(risk.risk_level)}
        </div>

        {/* CDC recommendation from API */}
        {risk.recommendations?.customer_summary && (
          <div
            style={{
              marginBottom: "12px",
              padding: "12px",
              backgroundColor: "rgba(255, 255, 255, 0.5)",
              borderRadius: "6px",
              border: "1px solid #e5e7eb",
            }}
          >
            <p style={{ fontSize: "14px", lineHeight: "1.6", margin: 0 }}>
              {risk.recommendations.customer_summary}
            </p>
          </div>
        )}

        {risk.additional_notes && (
          <p style={{ fontSize: "14px", marginBottom: "8px" }}>
            {risk.additional_notes}
          </p>
        )}

        {risk.recommendations?.prevention_strategies && (
          <div style={{ marginTop: "8px" }}>
            <p
              style={{
                fontSize: "14px",
                fontWeight: "500",
                marginBottom: "4px",
              }}
            >
              Prevention:
            </p>
            <ul
              style={{ fontSize: "14px", margin: "4px 0", paddingLeft: "20px" }}
            >
              {risk.recommendations.prevention_strategies.map(
                (strategy: string, idx: number) => (
                  <li key={idx} style={{ marginBottom: "2px" }}>
                    {strategy}
                  </li>
                ),
              )}
            </ul>
          </div>
        )}
      </div>
    );
  };

  // Calculate totals
  const totalCritical = priorityData.critical.length;
  const totalConditionalEntry = priorityData.conditionalEntry.length;
  const totalRecommended =
    (healthRecs.risk_summary?.important || 0) +
    (healthRecs.risk_summary?.essential || 0) +
    (healthRecs.medication_summary?.regular || 0);
  const totalConditional =
    (healthRecs.risk_summary?.consider || 0) +
    (healthRecs.conditional_medications?.length || 0);

  // Check if there are any recommendations at all
  const hasAnyRecommendations =
    totalCritical > 0 ||
    totalConditionalEntry > 0 ||
    priorityData.vaccines.required.length > 0 ||
    priorityData.vaccines.recommended.length > 0 ||
    priorityData.vaccines.conditional.length > 0 ||
    priorityData.medications.required.length > 0 ||
    priorityData.medications.conditional.length > 0 ||
    priorityData.environmental.length > 0;

  // If no recommendations, show empty state
  if (!hasAnyRecommendations) {
    return (
      <div style={{ width: "100%", maxWidth: "1024px", margin: "0 auto" }}>
        <div
          style={{
            backgroundColor: "white",
            borderRadius: "8px",
            boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.1)",
            padding: "40px 24px",
            textAlign: "center",
          }}
        >
          <div style={{ fontSize: "48px", marginBottom: "16px" }}>✅</div>
          <h3
            style={{
              fontSize: "18px",
              fontWeight: "600",
              color: "#374151",
              marginBottom: "8px",
            }}
          >
            No specific health recommendations
          </h3>
          <p
            style={{
              fontSize: "14px",
              color: "#6b7280",
              lineHeight: "1.5",
              maxWidth: "400px",
              margin: "0 auto",
            }}
          >
            There are no additional health recommendations for{" "}
            {destination.destination?.name || destination.name} at this time.
            Standard travel precautions should still be followed.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ width: "100%", maxWidth: "1024px", margin: "0 auto", overflow: "hidden" }}>
      <div
        style={{
          backgroundColor: "white",
          borderRadius: "8px",
          boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.1)",
          padding: "16px",
        }}
      >
        <h2
          style={{
            fontSize: "18px",
            fontWeight: "700",
            marginBottom: "16px",
            display: "flex",
            alignItems: "flex-start",
            gap: "8px",
            flexWrap: "wrap",
            lineHeight: "1.3",
            wordBreak: "break-word",
          }}
        >
          <span style={{ flexShrink: 0 }}>🛡️</span>
          <span>Health Recommendations for {destination.destination?.name || destination.name}</span>
        </h2>

        {/* Summary Stats - Clickable Cards */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(90px, 1fr))",
            gap: "8px",
            marginBottom: "16px",
          }}
        >
          {totalCritical > 0 && (
            <div
              onClick={() => toggleSection("critical")}
              style={{
                textAlign: "center",
                padding: "10px 8px",
                backgroundColor: "#fef2f2",
                borderRadius: "8px",
                cursor: "pointer",
                transition: "all 0.2s ease",
                border: "2px solid transparent",
                overflow: "hidden",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = "#fee2e2";
                e.currentTarget.style.borderColor = "#dc2626";
                e.currentTarget.style.transform = "translateY(-2px)";
                e.currentTarget.style.boxShadow =
                  "0 4px 12px rgba(220, 38, 38, 0.2)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "#fef2f2";
                e.currentTarget.style.borderColor = "transparent";
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "none";
              }}
            >
              <div
                style={{
                  fontSize: "24px",
                  fontWeight: "700",
                  color: "#dc2626",
                }}
              >
                {totalCritical}
              </div>
              <div
                style={{
                  fontSize: "11px",
                  color: "#991b1b",
                  fontWeight: "600",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                Required
              </div>
            </div>
          )}
          {totalConditionalEntry > 0 && (
            <div
              onClick={() => toggleSection("conditionalEntry")}
              style={{
                textAlign: "center",
                padding: "10px 8px",
                backgroundColor: "#fffbeb",
                borderRadius: "8px",
                cursor: "pointer",
                transition: "all 0.2s ease",
                border: "2px solid transparent",
                overflow: "hidden",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = "#fef3c7";
                e.currentTarget.style.borderColor = "#f59e0b";
                e.currentTarget.style.transform = "translateY(-2px)";
                e.currentTarget.style.boxShadow =
                  "0 4px 12px rgba(245, 158, 11, 0.2)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "#fffbeb";
                e.currentTarget.style.borderColor = "transparent";
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "none";
              }}
            >
              <div
                style={{
                  fontSize: "24px",
                  fontWeight: "700",
                  color: "#f59e0b",
                }}
              >
                {totalConditionalEntry}
              </div>
              <div
                style={{
                  fontSize: "11px",
                  color: "#92400e",
                  fontWeight: "600",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                Route-Based
              </div>
            </div>
          )}
          {(priorityData.vaccines.required.length > 0 ||
            priorityData.vaccines.recommended.length > 0) && (
            <div
              onClick={() => toggleSection("vaccines")}
              style={{
                textAlign: "center",
                padding: "10px 8px",
                backgroundColor: "#fefce8",
                borderRadius: "8px",
                cursor: "pointer",
                transition: "all 0.2s ease",
                border: "2px solid transparent",
                overflow: "hidden",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = "#fef9c3";
                e.currentTarget.style.borderColor = "#eab308";
                e.currentTarget.style.transform = "translateY(-2px)";
                e.currentTarget.style.boxShadow =
                  "0 4px 12px rgba(234, 179, 8, 0.2)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "#fefce8";
                e.currentTarget.style.borderColor = "transparent";
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "none";
              }}
            >
              <div
                style={{
                  fontSize: "24px",
                  fontWeight: "700",
                  color: "#eab308",
                }}
              >
                {totalRecommended}
              </div>
              <div
                style={{
                  fontSize: "11px",
                  color: "#713f12",
                  fontWeight: "600",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                Recommended
              </div>
            </div>
          )}
          {totalConditional > 0 && (
            <div
              onClick={() => toggleSection("vaccines")}
              style={{
                textAlign: "center",
                padding: "10px 8px",
                backgroundColor: "#eff6ff",
                borderRadius: "8px",
                cursor: "pointer",
                transition: "all 0.2s ease",
                border: "2px solid transparent",
                overflow: "hidden",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = "#dbeafe";
                e.currentTarget.style.borderColor = "#2563eb";
                e.currentTarget.style.transform = "translateY(-2px)";
                e.currentTarget.style.boxShadow =
                  "0 4px 12px rgba(37, 99, 235, 0.2)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "#eff6ff";
                e.currentTarget.style.borderColor = "transparent";
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "none";
              }}
            >
              <div
                style={{
                  fontSize: "24px",
                  fontWeight: "700",
                  color: "#2563eb",
                }}
              >
                {totalConditional}
              </div>
              <div
                style={{
                  fontSize: "11px",
                  color: "#1e40af",
                  fontWeight: "600",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                Conditional
              </div>
            </div>
          )}
          {(priorityData.medications.required.length > 0 ||
            priorityData.medications.conditional.length > 0 ||
            priorityData.environmental.length > 0) && (
            <div
              onClick={() => {
                if (
                  priorityData.medications.required.length > 0 ||
                  priorityData.medications.conditional.length > 0
                ) {
                  toggleSection("medications");
                } else if (priorityData.environmental.length > 0) {
                  toggleSection("environmental");
                }
              }}
              style={{
                textAlign: "center",
                padding: "10px 8px",
                backgroundColor: "#f0fdf4",
                borderRadius: "8px",
                cursor: "pointer",
                transition: "all 0.2s ease",
                border: "2px solid transparent",
                overflow: "hidden",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = "#dcfce7";
                e.currentTarget.style.borderColor = "#16a34a";
                e.currentTarget.style.transform = "translateY(-2px)";
                e.currentTarget.style.boxShadow =
                  "0 4px 12px rgba(22, 163, 74, 0.2)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "#f0fdf4";
                e.currentTarget.style.borderColor = "transparent";
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "none";
              }}
            >
              <div
                style={{
                  fontSize: "24px",
                  fontWeight: "700",
                  color: "#16a34a",
                }}
              >
                {priorityData.medications.required.length +
                  priorityData.medications.conditional.length +
                  priorityData.environmental.length}
              </div>
              <div
                style={{
                  fontSize: "11px",
                  color: "#166534",
                  fontWeight: "600",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                Health Risks
              </div>
            </div>
          )}
        </div>

        {/* Critical Vaccines - Required for Entry */}
        {priorityData.critical.length > 0 && (
          <div id="section-critical" style={{ marginBottom: "24px" }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexWrap: "wrap",
                gap: "8px",
                cursor: "pointer",
                padding: "12px",
                backgroundColor: "#fef2f2",
                borderRadius: expandedSections.critical ? "8px 8px 0 0" : "8px",
                border: "2px solid #dc2626",
                borderBottom: expandedSections.critical
                  ? "none"
                  : "2px solid #dc2626",
              }}
              onClick={() => toggleSection("critical")}
            >
              <h3
                style={{
                  fontSize: "18px",
                  fontWeight: "600",
                  color: "#7f1d1d",
                  margin: 0,
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                ⚠️ Required for Entry
              </h3>
              <span style={{ fontSize: "20px" }}>
                {expandedSections.critical ? "▲" : "▼"}
              </span>
            </div>
            {expandedSections.critical && (
              <div
                style={{
                  border: "2px solid #dc2626",
                  borderTop: "none",
                  borderRadius: "0 0 8px 8px",
                  padding: "16px",
                }}
              >
                {priorityData.critical.map(renderVaccineCard)}
              </div>
            )}
          </div>
        )}

        {/* Conditional Entry Requirements */}
        {priorityData.conditionalEntry.length > 0 && (
          <div id="section-conditionalEntry" style={{ marginBottom: "24px" }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexWrap: "wrap",
                gap: "8px",
                cursor: "pointer",
                padding: "12px",
                backgroundColor: "#fffbeb",
                borderRadius: expandedSections.conditionalEntry
                  ? "8px 8px 0 0"
                  : "8px",
                border: "2px solid #f59e0b",
                borderBottom: expandedSections.conditionalEntry
                  ? "none"
                  : "2px solid #f59e0b",
              }}
              onClick={() => toggleSection("conditionalEntry")}
            >
              <h3
                style={{
                  fontSize: "18px",
                  fontWeight: "600",
                  color: "#92400e",
                  margin: 0,
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                🌍 Entry Requirements Based on Travel Route
              </h3>
              <span style={{ fontSize: "20px" }}>
                {expandedSections.conditionalEntry ? "▲" : "▼"}
              </span>
            </div>
            {expandedSections.conditionalEntry && (
              <div
                style={{
                  border: "2px solid #f59e0b",
                  borderTop: "none",
                  borderRadius: "0 0 8px 8px",
                  padding: "16px",
                }}
              >
                {priorityData.conditionalEntry.map(renderVaccineCard)}
              </div>
            )}
          </div>
        )}

        {/* Medications Section */}
        {(priorityData.medications.required.length > 0 ||
          priorityData.medications.conditional.length > 0) && (
          <div id="section-medications" style={{ marginBottom: "24px" }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexWrap: "wrap",
                gap: "8px",
                cursor: "pointer",
                padding: "12px",
                backgroundColor: "#fefce8",
                borderRadius: expandedSections.medications
                  ? "8px 8px 0 0"
                  : "8px",
                border: "2px solid #eab308",
                borderBottom: expandedSections.medications
                  ? "none"
                  : "2px solid #eab308",
              }}
              onClick={() => toggleSection("medications")}
            >
              <h3
                style={{
                  fontSize: "18px",
                  fontWeight: "600",
                  color: "#713f12",
                  margin: 0,
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                🦟 Disease Prevention Medications
              </h3>
              <span style={{ fontSize: "20px" }}>
                {expandedSections.medications ? "▲" : "▼"}
              </span>
            </div>
            {expandedSections.medications && (
              <div
                style={{
                  border: "2px solid #eab308",
                  borderTop: "none",
                  borderRadius: "0 0 8px 8px",
                  padding: "16px",
                }}
              >
                {priorityData.medications.required.map(renderMedicationSection)}
                {priorityData.medications.conditional.map(
                  renderMedicationSection,
                )}
              </div>
            )}
          </div>
        )}

        {/* Environmental Risks */}
        {priorityData.environmental.length > 0 && (
          <div id="section-environmental" style={{ marginBottom: "24px" }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexWrap: "wrap",
                gap: "8px",
                cursor: "pointer",
                padding: "12px",
                backgroundColor: "#fffbeb",
                borderRadius: expandedSections.environmental
                  ? "8px 8px 0 0"
                  : "8px",
                border: "2px solid #f59e0b",
                borderBottom: expandedSections.environmental
                  ? "none"
                  : "2px solid #f59e0b",
              }}
              onClick={() => toggleSection("environmental")}
            >
              <h3
                style={{
                  fontSize: "18px",
                  fontWeight: "600",
                  color: "#92400e",
                  margin: 0,
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                ⛰️ Environmental Health Risks
              </h3>
              <span style={{ fontSize: "20px" }}>
                {expandedSections.environmental ? "▲" : "▼"}
              </span>
            </div>
            {expandedSections.environmental && (
              <div
                style={{
                  border: "2px solid #f59e0b",
                  borderTop: "none",
                  borderRadius: "0 0 8px 8px",
                  padding: "16px",
                }}
              >
                {priorityData.environmental.map(renderEnvironmentalRisk)}
              </div>
            )}
          </div>
        )}

        {/* Other Vaccines */}
        {(priorityData.vaccines.required.length > 0 ||
          priorityData.vaccines.recommended.length > 0 ||
          priorityData.vaccines.conditional.length > 0) && (
          <div id="section-vaccines" style={{ marginBottom: "24px" }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexWrap: "wrap",
                gap: "8px",
                cursor: "pointer",
                padding: "12px",
                backgroundColor: "#eff6ff",
                borderRadius: expandedSections.vaccines ? "8px 8px 0 0" : "8px",
                border: "2px solid #2563eb",
                borderBottom: expandedSections.vaccines
                  ? "none"
                  : "2px solid #2563eb",
              }}
              onClick={() => toggleSection("vaccines")}
            >
              <h3
                style={{
                  fontSize: "18px",
                  fontWeight: "600",
                  color: "#1e40af",
                  margin: 0,
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                💉 Other Vaccine Recommendations
              </h3>
              <span style={{ fontSize: "20px" }}>
                {expandedSections.vaccines ? "▲" : "▼"}
              </span>
            </div>
            {expandedSections.vaccines && (
              <div
                style={{
                  border: "2px solid #2563eb",
                  borderTop: "none",
                  borderRadius: "0 0 8px 8px",
                  padding: "16px",
                }}
              >
                {priorityData.vaccines.required.map(renderVaccineCard)}
                {priorityData.vaccines.recommended.map(renderVaccineCard)}
                {priorityData.vaccines.conditional.map(renderVaccineCard)}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default HealthRecommendations;
